import { supabase } from './supabase';

/**
 * Réservation Cal.com telle que le serveur l'a enregistrée (edge function `cal-webhook`).
 * C'est la SEULE preuve qu'un créneau existe réellement dans l'agenda de Julien : l'URL de
 * confirmation vue par la WebView est une information client, elle ne fait pas foi.
 */
export interface CoachingBooking {
  booking_uid: string;
  status: 'accepted' | 'cancelled' | 'rescheduled';
  starts_at: string | null;
  meeting_url: string | null;
}

export async function getCoachingBooking(uid: string): Promise<CoachingBooking | null> {
  const { data, error } = await supabase
    .from('coaching_bookings')
    .select('booking_uid, status, starts_at, meeting_url')
    .eq('booking_uid', uid)
    .maybeSingle();

  // Une erreur réseau n'est pas une absence de réservation : on la remonte pour que l'appelant
  // puisse réessayer plutôt que d'afficher « créneau introuvable » à tort.
  if (error) throw error;
  return (data as CoachingBooking | null) ?? null;
}

export interface WaitOptions {
  attempts?: number;
  delayMs?: number;
  /** Permet d'abandonner proprement si l'utilisateur quitte l'écran. */
  isCancelled?: () => boolean;
}

/**
 * Attend que le webhook Cal.com ait créé la ligne. Le webhook arrive en général en une à deux
 * secondes, mais il part APRÈS la redirection de confirmation : sans cette attente, l'écran de
 * paiement afficherait « créneau introuvable » pendant le temps normal du réseau.
 */
export async function waitForCoachingBooking(
  uid: string,
  { attempts = 8, delayMs = 1500, isCancelled }: WaitOptions = {},
): Promise<CoachingBooking | null> {
  for (let i = 0; i < attempts; i++) {
    if (isCancelled?.()) return null;
    try {
      const booking = await getCoachingBooking(uid);
      if (booking) return booking;
    } catch {
      // Erreur transitoire : on laisse la boucle réessayer plutôt que d'échouer au premier accroc.
    }
    if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
  }
  return null;
}
